import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { getEmployeePayslip } from '../../api/payroll';

const PayslipView = () => {
  const { payrollId, employeeId } = useParams();
  const navigate = useNavigate();


  const [payslipData, setPayslipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState(null);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 0);
  };

  const handleDownload = useCallback(() => {
    window.print();
  }, [payslipData]);

  const fetchPayslipData = useCallback(async () => {
    if (!payrollId || !employeeId || payrollId === "undefined" || employeeId === "undefined") {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await getEmployeePayslip(payrollId, employeeId);

      if (response.status === "success") {
        setPayslipData(response.data);

        const company = response.data.assignment.company;
        let displayCompany;
        if (company.includes("7-Star")) {
          displayCompany = "7-Star Manpower Services";
        } else if (company.includes("Syzygy")) {
          displayCompany = "Syzygy Staffing Resources";
        } else if (company.includes("Sevenstarjasem")) {
          displayCompany = "Seven Star Jasem Services";
        } else {
          displayCompany = company;
        }
        setCompanyName(displayCompany);

        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get("print") === "true") {
          setTimeout(() => window.print(), 500);
        }
      } else {
        setError(response.message || "Failed to fetch payslip data");
      }
    } catch (err) {
      console.error("Error fetching payslip data:", err);
      setError("Failed to fetch payslip data");
    } finally {
      setLoading(false);
    }
  }, [payrollId, employeeId]);

  useEffect(() => {
    if (payrollId && employeeId && payrollId !== "undefined" && employeeId !== "undefined") {
      fetchPayslipData();
    } else {
      console.error("Invalid parameters:", { payrollId, employeeId });
      setError("Invalid payroll or employee ID");
      setLoading(false);
    }
  }, [payrollId, employeeId, fetchPayslipData]);

  useEffect(() => {
    const onPayslipSettingsUpdated = (e) => {
      const id = e.detail?.payrollId;
      if (id != null && String(id) === String(payrollId)) {
        fetchPayslipData();
      }
    };
    window.addEventListener("payroll-payslip-settings-updated", onPayslipSettingsUpdated);
    return () => window.removeEventListener("payroll-payslip-settings-updated", onPayslipSettingsUpdated);
  }, [payrollId, fetchPayslipData]);

  // Removed automatic print trigger - now only prints when user explicitly requests it

  // Listen for messages from parent window (dialog)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.action === 'download') {

        handleDownload();
      } else if (event.data && event.data.action === 'print') {
        handlePrint();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleDownload]);
  const generatePrintContent = () => {
    if (!payslipData) return '';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslip - ${payslipData.employee.name}</title>
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #333;
            }
            .payslip-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
            }
            .header {
              background: linear-gradient(135deg, #12C1DC, #0FABDB);
              color: white;
              padding: 20px;
              text-align: center;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: bold;
            }
            .employee-info {
              padding: 20px;
              background: #f8f9fa;
              border-bottom: 2px solid #e5e7eb;
            }
            .employee-info h2 {
              margin: 0 0 10px 0;
              color: #2c3e50;
              font-size: 20px;
            }
            .employee-info p {
              margin: 5px 0;
              color: #6b7280;
            }
            .payslip-content {
              padding: 20px;
            }
            .earnings-deductions {
              display: flex;
              gap: 20px;
              margin-bottom: 20px;
            }
            .earnings, .deductions {
              flex: 1;
            }
            .earnings h3, .deductions h3 {
              background: #12C1DC;
              color: white;
              padding: 10px;
              margin: 0 0 10px 0;
              text-align: center;
              font-size: 16px;
            }
            .earnings-table, .deductions-table {
              width: 100%;
              border-collapse: collapse;
            }
            .earnings-table td, .deductions-table td {
              padding: 8px 12px;
              border-bottom: 1px solid #e5e7eb;
            }
            .earnings-table tr:nth-child(even), .deductions-table tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .earnings-table tr:nth-child(odd), .deductions-table tr:nth-child(odd) {
              background-color: #ffffff;
            }
            .earnings-table tr:hover, .deductions-table tr:hover {
              background-color: #e9ecef !important;
            }
            .earnings-table td:last-child, .deductions-table td:last-child {
              text-align: right;
              font-weight: 600;
            }
            .summary {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin: 20px 0;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .summary-row:last-child {
              border-bottom: none;
              font-weight: bold;
              font-size: 18px;
              color: #10b981;
            }
            .acknowledgement {
              margin-top: 30px;
              text-align: center;
            }
            .acknowledgement h4 {
              margin: 0 0 10px 0;
              color: #2c3e50;
            }
            .signature-line {
              border-bottom: 1px solid #333;
              width: 200px;
              margin: 20px auto 5px auto;
            }
            .signature-label {
              font-size: 12px;
              color: #6b7280;
            }
            /* Tight mobile layout: keep the 2-column structure, just reduce padding/font sizes. */
            @media screen and (max-width: 480px) {
              .payslip-container {
                max-width: 100% !important;
                margin: 0 !important;
                box-shadow: none !important;
              }
              .header {
                padding: 14px 10px;
              }
              .header h1 {
                font-size: 18px;
              }
              .employee-info {
                padding: 14px 10px;
              }
              .employee-info h2 {
                font-size: 16px;
              }
              .payslip-content {
                padding: 12px 10px;
              }
              .earnings-deductions {
                gap: 10px;
                margin-bottom: 12px;
              }
              .earnings h3, .deductions h3 {
                padding: 8px 6px;
                margin: 0 0 8px 0;
                font-size: 14px;
              }
              .earnings-table td, .deductions-table td {
                padding: 6px 8px;
                font-size: 12px;
              }
              .summary {
                padding: 14px 10px;
                margin: 12px 0;
              }
              .summary-row {
                padding: 6px 0;
                font-size: 13px;
              }
              .summary-row:last-child {
                font-size: 16px;
              }
              .acknowledgement {
                margin-top: 18px;
              }
              .signature-line {
                width: 140px;
                margin: 12px auto 4px auto;
              }
            }
            @media print {
              .no-print { display: none !important; }
              body { margin: 0; }
              .payslip-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            <div class="header">
              <h1>${companyName}</h1>
            </div>
            
            <div class="employee-info">
              <h2>EMPLOYEE: ${payslipData.employee.name}</h2>
              <p><strong>ASSIGNED AT:</strong> ${payslipData.assignment.company}</p>
              <p><strong>DATE COVERED:</strong> ${payslipData.assignment.dateCovered}</p>
            </div>

            <div class="payslip-content">
              <div class="earnings-deductions">
                <div class="earnings">
                  <h3>EARNINGS</h3>
                  <table class="earnings-table">
                    <tr>
                      <td>REGULAR HOURS:</td>
                      <td>${payslipData.earnings.regularHours.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>TOTAL DAYS:</td>
                      <td>${payslipData.earnings.totalDays.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>LATE AMOUNT:</td>
                      <td>${payslipData.earnings.lateMinutes.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>REGULAR PAY:</td>
                      <td>₱${payslipData.earnings.regularPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td>OVERTIME PAY:</td>
                      <td>₱${payslipData.earnings.overtimePay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td>REST DAY PAY:</td>
                      <td>₱${payslipData.earnings.restDayPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    ${payslipData?.earnings?.restDayPayOt > 0 ? `
                    <tr>
                      <td>REST DAY OT PAY:</td>
                      <td>₱${payslipData.earnings.restDayPayOt.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}
                    <tr>
                      <td>NIGHT DIFF:</td>
                      <td>₱${payslipData.earnings.nightDiff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td>LEGAL HOLIDAY:</td>
                      <td>₱${payslipData.earnings.legalHoliday.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    ${payslipData?.earnings?.legalHolidayRd > 0 ? `
                    <tr>
                      <td>LEGAL HOLIDAY RD:</td>
                      <td>&#8369;${payslipData.earnings.legalHolidayRd.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</td>
                    </tr>
                    ` : ''}
                    ${payslipData?.earnings?.legalHolidayNsd > 0 ? `
                    <tr>
                      <td>LEGAL HOLIDAY NSD:</td>
                      <td>&#8369;${payslipData.earnings.legalHolidayNsd.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</td>
                    </tr>
                    ` : ''}
                    ${payslipData?.earnings?.legalHolidayOt > 0 ? `
                    <tr>
                      <td>LEGAL HOLIDAY OT:</td>
                      <td>₱${payslipData.earnings.legalHolidayOt.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}
                    <tr>
                      <td>SPECIAL HOLIDAY:</td>
                      <td>₱${payslipData.earnings.specialHoliday.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    ${payslipData?.earnings?.specialHolidayOt > 0 ? `
                    <tr>
                      <td>SPECIAL HOLIDAY OT:</td>
                      <td>₱${payslipData.earnings.specialHolidayOt.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}
                    ${Array.isArray(payslipData?.earnings?.allowanceBreakdown) && payslipData.earnings.allowanceBreakdown.length > 0 ? `
                    <tr>
                      <td colspan="2" style="font-weight: 600; padding-top: 8px; text-align: center;">ALLOWANCES</td>
                    </tr>
                    ${payslipData.earnings.allowanceBreakdown.map((line) => `
                    <tr>
                      <td>${line.name}${line.source ? ` (${line.source})` : ''}:</td>
                      <td>₱${Number(line.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    `).join('')}
                    ` : ''}
                    <tr>
                      <td>OTHER INCOME:</td>
                      <td>₱${payslipData.earnings.otherIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr style="border-top: 2px solid #12C1DC; font-weight: bold;">
                      <td>GROSS PAY:</td>
                      <td>₱${payslipData.earnings.grossPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  </table>
                </div>

                <div class="deductions">
                  <h3>DEDUCTIONS</h3>
                  <table class="deductions-table">
                    <tr>
                      <td>SSS:</td>
                      <td>₱${payslipData.deductions.sss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td>PHILHEALTH:</td>
                      <td>₱${payslipData.deductions.philhealth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td>PAGIBIG:</td>
                      <td>₱${payslipData.deductions.pagibig.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>

                    ${payslipData?.deductions?.sssLoan > 0 ? `
                    <tr>
                      <td>SSS LOAN:</td>
                      <td>₱${payslipData.deductions.sssLoan.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}
                    ${payslipData.deductions.sssCalamityLoan > 0 ? `
                    <tr>
                      <td>SSS CALAMITY LOAN:</td>
                      <td>₱${payslipData.deductions.sssCalamityLoan.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}
                    ${payslipData.deductions.pagibigLoan > 0 ? `
                    <tr>
                      <td>PAGIBIG LOAN:</td>
                      <td>₱${payslipData.deductions.pagibigLoan.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}
                    ${payslipData.deductions.pagibigCalamityLoan > 0 ? `
                    <tr>
                      <td>PAGIBIG CALAMITY LOAN:</td>
                      <td>₱${payslipData.deductions.pagibigCalamityLoan.toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}</td>
                    </tr>
                    ` : ''}

                    ${(payslipData?.deductions?.customDeductions || []).map((item) => `
                    <tr>
                      <td>${String(item.displayLabel || item.deductionType || '').toUpperCase()}:</td>
                      <td>PHP ${Number(item.amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}</td>
                    </tr>
                    `).join('')}
                    <tr>
                      <td>OTHER DEDUCTIONS:</td>
                      <td>₱${payslipData.deductions.otherDeductions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr style="border-top: 2px solid #12C1DC; font-weight: bold;">
                      <td>TOTAL DEDUCTIONS:</td>
                      <td>₱${payslipData.deductions.totalDeductions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  </table>
                </div>
              </div>

              <div class="summary">
                <div class="summary-row">
                  <span>GROSS PAY:</span>
                  <span>₱${payslipData.summary.grossPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div class="summary-row">
                  <span>TOTAL DEDUCTIONS:</span>
                  <span>₱${payslipData.summary.totalDeductions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div class="summary-row">
                  <span>NET PAY:</span>
                  <span>
                    ₱${Math.max(0, payslipData.summary.netPay)
                        .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div class="acknowledgement">
                <h4>ACKNOWLEDGEMENT</h4>
                <p>I HAVE READ AND UNDERSTOOD THE ABOVE COMPUTATIONS OF MY SALARY.</p>
                <div class="signature-line"></div>
                <p class="signature-label">Signature with Date</p>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#12C1DC' }} />
        <Typography variant="h6" sx={{ color: '#6b7280' }}>
          Loading payslip data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            if (window.location.pathname.startsWith('/print/')) {
              window.close();
            } else {
              navigate(-1);
            }
          }}
          sx={{ bgcolor: '#12C1DC', '&:hover': { bgcolor: '#0FABDB' } }}
        >
          {window.location.pathname.startsWith('/print/') ? 'Close Window' : 'Go Back'}
        </Button>
      </Box>
    );
  }

  if (!payslipData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No payslip data found
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            if (window.location.pathname.startsWith('/print/')) {
              window.close();
            } else {
              navigate(-1);
            }
          }}
          sx={{ bgcolor: '#12C1DC', '&:hover': { bgcolor: '#0FABDB' } }}
        >
          {window.location.pathname.startsWith('/print/') ? 'Close Window' : 'Go Back'}
        </Button>
      </Box>
    );
  }

  // Render the payslip content
  return (
    <Box sx={{ 
      width: '100%', 
      minHeight: '100vh',
      bgcolor: 'white',
      p: 2
    }}>
      <div 
        dangerouslySetInnerHTML={{ __html: generatePrintContent() }}
        style={{ width: '100%' }}
      />
    </Box>
  );
};

export default PayslipView;


